import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'dva';
import _ from 'lodash';
import { useHistory, useIntl } from 'umi';

import { colDefFilteredValue, getFiltersGlobalFilters } from '@/utils/infrastructure';

/** Stable fallback — `?? {}` inline creates a new ref every selector run and can loop useSelector. */
const EMPTY_ACTIVATING_ITEM_IDS = Object.freeze({});

/**
 * Generic list controller for Configuration CRUD modules.
 */
export function useConfigListController({
  modelName,
  defaultOrderBy = 'name asc',
  buildColumns,
  columnActions,
  select,
  searchSelect,
  notSelectSearch,
  orderBy,
  global,
  pagination,
  loading,
  list,
  globalFilters,
  defaultModuleFilters,
}) {
  const [columns, setColumns] = useState([]);
  const [loadedComponent, setLoadedComponent] = useState(false);
  // Holds route-entry defaults until the globalFilters dispatch lands, so a
  // follow-up refetch does not drop them.
  const pendingModuleFiltersRef = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { searchCriteria, pageSize } = global;
  const activatingItemIds = useSelector(
    (state) => state[modelName]?.activatingItemIds ?? EMPTY_ACTIVATING_ITEM_IDS,
  );

  const getParams = useCallback(
    ({ page = null } = {}) => {
      const pending = pendingModuleFiltersRef.current;
      const filtersInput = pending
        ? {
            ...(globalFilters && typeof globalFilters === 'object' && !Array.isArray(globalFilters)
              ? globalFilters
              : {}),
            [modelName]: pending,
          }
        : globalFilters;
      const filters = getFiltersGlobalFilters(filtersInput, modelName);
      return {
        ...pagination,
        ...(pageSize && { page: page || pagination.page, limit: pageSize }),
        ...(searchCriteria && { search: searchCriteria }),
        ...(orderBy !== null && { orderBy: orderBy ? `${orderBy}` : defaultOrderBy }),
        ...(filters && { filter: filters }),
        select,
      };
    },
    [
      globalFilters,
      modelName,
      pagination,
      pageSize,
      searchCriteria,
      orderBy,
      defaultOrderBy,
      select,
    ],
  );

  const getExportParams = useCallback(() => {
    const filters = getFiltersGlobalFilters(globalFilters, modelName);
    return {
      ...(searchCriteria && { search: searchCriteria }),
      ...(orderBy !== null && { orderBy: orderBy ? `${orderBy}` : defaultOrderBy }),
      ...(filters && { filter: filters }),
      ...(select && { select }),
      ...(searchSelect && { searchSelect }),
      ...(notSelectSearch && { notSelectSearch }),
    };
  }, [
    globalFilters,
    modelName,
    searchCriteria,
    orderBy,
    select,
    searchSelect,
    notSelectSearch,
    defaultOrderBy,
  ]);

  const loadData = useCallback(
    (params) => {
      dispatch({
        type: `${modelName}/fetchList`,
        payload: params,
      });
    },
    [dispatch, modelName],
  );

  const loadDataPaginate = useCallback(
    (p) => {
      const params = _.merge(getParams({ page: null }), p);
      loadData(params);
    },
    [getParams, loadData],
  );

  const setOrderBy = useCallback(
    (nextOrderBy) => {
      dispatch({
        type: `${modelName}/setOrderBy`,
        payload: nextOrderBy,
      });
    },
    [dispatch, modelName],
  );

  const openDrawerCreateOrEdit = useCallback(
    (id, detail = false, item = null) => {
      dispatch({
        type: `${modelName}/showForm`,
        payload: {
          state: true,
          id,
          item,
          detail,
        },
      });
    },
    [dispatch, modelName],
  );

  const deleteItemConfirm = useCallback(
    (id) => {
      dispatch({
        type: 'global/modalConfirm',
        payload: {
          state: true,
          id,
          modelDelete: modelName,
        },
      });
    },
    [dispatch],
  );

  const confirm = useCallback(
    (id, view = false) => {
      const params = getParams({ page: 1 });
      dispatch({
        type: `${modelName}/fetchDelete`,
        payload: {
          id: id || global.deleteId,
          view,
          params,
        },
      });
    },
    [dispatch, modelName, getParams, global.deleteId],
  );

  const activate = useCallback(
    (id, state) => {
      dispatch({
        type: `${modelName}/fetchActivate`,
        payload: { id, state },
      });
    },
    [dispatch, modelName],
  );

  const resolvedColumnActions = useMemo(() => {
    if (typeof columnActions === 'function') {
      return columnActions({ dispatch, modelName }) || {};
    }
    return columnActions || {};
  }, [columnActions, dispatch, modelName]);

  useEffect(() => {
    if (!loadedComponent || loading) return;
    loadData(getParams({ page: 1 }));
    // Refetch only when filter/sort/pageSize inputs change — not when loading flips or pagination updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, searchCriteria, orderBy, globalFilters]);

  useEffect(() => {
    const pending = pendingModuleFiltersRef.current;
    if (pending && _.isEqual(globalFilters?.[modelName], pending)) {
      pendingModuleFiltersRef.current = null;
    }
  }, [globalFilters, modelName]);

  // Initial fetch when entering the module route; clears search/filters once per navigation.
  // Modules with defaultModuleFilters reopen on those values (users → enabled only).
  useEffect(() => {
    if (orderBy === '') {
      setOrderBy(defaultOrderBy);
    }

    const entryFilter = defaultModuleFilters
      ? getFiltersGlobalFilters({ [modelName]: defaultModuleFilters }, modelName)
      : null;

    if (defaultModuleFilters) {
      pendingModuleFiltersRef.current = defaultModuleFilters;
      dispatch({
        type: 'global/globalFiltersState',
        payload: defaultModuleFilters,
        modelName,
      });
    } else {
      pendingModuleFiltersRef.current = null;
    }

    loadData({
      ...getParams({ page: 1 }),
      search: null,
      filter: entryFilter,
    });
    setLoadedComponent(true);
    return () => {
      setLoadedComponent(false);
      pendingModuleFiltersRef.current = null;
    };
    // Intentionally scoped to route entry only — avoid resetting search/filters on sort/filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);

  useEffect(() => {
    if (!buildColumns) return;

    const colConfig = buildColumns({
      formatMessage,
      searchCriteria,
      openDrawerCreateOrEdit,
      deleteItemConfirm,
      activate,
      activatingItemIds,
      orderBy,
      defaultOrderBy,
      list,
      ...resolvedColumnActions,
    });

    const orderByForColumns = orderBy === null ? null : orderBy || defaultOrderBy;
    setColumns(colDefFilteredValue(colConfig, orderByForColumns));
  }, [
    buildColumns,
    formatMessage,
    history.location.pathname,
    orderBy,
    searchCriteria,
    defaultOrderBy,
    openDrawerCreateOrEdit,
    deleteItemConfirm,
    activate,
    activatingItemIds,
    list,
    resolvedColumnActions,
  ]);

  const confirmFromView = useCallback(
    (id) => {
      confirm(id, true);
    },
    [confirm],
  );

  return {
    columns,
    loadDataPaginate,
    confirm,
    confirmFromView,
    setOrderBy,
    openDrawerCreateOrEdit,
    defaultOrderBy,
    activate,
    getExportParams,
  };
}

export default useConfigListController;
